import React from 'react';
import { Link } from 'react-router-dom';
import { MdSpeed, MdLocalGasStation, MdEco, MdSettings, MdLocationOn, MdFavorite, MdFavoriteBorder, MdPublic, MdTrendingDown, MdTrendingUp, MdRemove } from 'react-icons/md';
import { formatPrice, formatMileage, getFuelTypeDisplay } from '../mysearchConstants';

const PLACEHOLDER = '/images/placeholder.jpg';

const priceTagConfig: Record<string, { bg: string; color: string; label: string; icon: React.ReactNode }> = {
  GOOD_PRICE: { bg: '#F3F4F6', color: '#059669', label: 'Good Price', icon: <MdTrendingDown size={13} /> },
  FAIR_PRICE: { bg: '#F3F4F6', color: '#6B7280', label: 'Fair Price', icon: <MdRemove size={13} /> },
  HIGH_PRICE: { bg: '#F3F4F6', color: '#DC2626', label: 'High Price', icon: <MdTrendingUp size={13} /> },
};

interface VehicleCardProps {
  listing: any;
  isFavorited: boolean;
  isTogglingFavorite: boolean;
  onToggleFavorite: (id: string, e: React.MouseEvent) => void;
}

const VehicleCard: React.FC<VehicleCardProps> = ({
  listing,
  isFavorited,
  isTogglingFavorite,
  onToggleFavorite,
}) => {
  const photo = listing.thumbnailUrl || listing.image || PLACEHOLDER;
  const isPromoted = listing.promotionLevel && listing.promotionLevel !== 'STANDARD';
  const priceTag = listing.priceTag ? priceTagConfig[listing.priceTag] : null;
  const href = `/listing/${listing.slug || listing.id}`;
  const isElectric = listing.fuelType?.toLowerCase() === 'electric';
  const isImportedAndElectric = isElectric && !!listing.isImported;

  return (
    <Link
      to={href}
      className={`vc-card${isImportedAndElectric ? ' vc-card--imported-electric' : isElectric ? ' vc-card--electric' : ''}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {/* Image */}
      <div className="vc-img-wrapper">
        <img
          src={photo}
          alt={listing.title || `${listing.year} ${listing.make} ${listing.model}`}
          className="vc-img"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
        />
        {isPromoted && <span className="vc-badge vc-badge-top">TOP</span>}
        <button
          className={`vc-fav-btn ${isFavorited ? 'vc-fav-btn-active' : ''}`}
          onClick={e => onToggleFavorite(listing.id, e)}
          disabled={isTogglingFavorite}
          aria-label="Save to favorites"
        >
          {isTogglingFavorite
            ? <span className="vc-fav-spinner" />
            : isFavorited
              ? <MdFavorite size={18} />
              : <MdFavoriteBorder size={18} />}
        </button>
      </div>

      {/* Body */}
      <div className="vc-body">
        <h3 className="vc-title">
          {listing.year} {listing.make} {listing.model}
        </h3>

        {/* Specs pills */}
        <div className="vc-specs">
          {listing.mileage != null && (
            <span className="vc-pill">
              <MdSpeed size={12} /> {formatMileage(listing.mileage)} km
            </span>
          )}
          {listing.fuelType && (
            <span className={`vc-pill${isElectric ? ' vc-pill--electric' : ''}`}>
              {isElectric ? <MdEco size={12} /> : <MdLocalGasStation size={12} />}
              {getFuelTypeDisplay(listing.fuelType)}
            </span>
          )}
          {listing.transmission && (
            <span className="vc-pill">
              <MdSettings size={12} /> {listing.transmission}
            </span>
          )}
          {(listing.city || listing.location) && (
            <span className="vc-pill">
              <MdLocationOn size={12} /> {listing.city || listing.location}
            </span>
          )}
          {listing.isImported && (
            <span className="vc-pill vc-pill--imported">
              <MdPublic size={12} /> Imported
            </span>
          )}
        </div>

        {/* Price */}
        <div className="vc-price-row">
          <span className="vc-price">{formatPrice(listing.price)}</span>
          {priceTag && (
            <span
              className="vc-price-tag"
              style={{ background: priceTag.bg, color: priceTag.color, display: 'inline-flex', alignItems: 'center', gap: 3 }}
            >
              {priceTag.icon}{priceTag.label}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default VehicleCard;
